import React from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Alert,
  Button,
  Card,
  CardContent,
  Grid,
  Stepper,
  Step,
  StepLabel,
  Divider
} from '@mui/material';
import {
  CheckCircle,
  Schedule,
  Email,
  Phone,
  Description,
  Security,
  ArrowBack
} from '@mui/icons-material';

interface VerificacionEnviadaProps {
  verificacionId: string;
  estado: 'VERIFICADO' | 'PENDIENTE_APROBACION' | 'RECHAZADO';
  automatica: boolean;
  onfidoResult?: any;
  onVolver: () => void;
  onContinuar?: () => void;
}

const VerificacionEnviada: React.FC<VerificacionEnviadaProps> = ({
  verificacionId,
  estado,
  automatica,
  onfidoResult,
  onVolver,
  onContinuar
}) => {
  const steps = [
    'Verificación Enviada',
    'Revisión en Proceso',
    'Aprobación',
    'Firma del Documento'
  ];

  const getCurrentStep = () => {
    if (estado === 'VERIFICADO') return 3;
    if (estado === 'RECHAZADO') return 0; // Volver al inicio si fue rechazado
    return 1;
  };

  const getTiempoEstimado = () => {
    if (automatica) return 'Inmediato';
    return '24-48 horas hábiles';
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      display: 'flex', 
      flexDirection: 'column',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
    }}>
      {/* Header con branding */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
        color: 'white',
        py: 3,
        textAlign: 'center'
      }}>
        <Container maxWidth="md">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
            <Box sx={{ 
              width: 60, 
              height: 60, 
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 2
            }}>
              <Security sx={{ fontSize: 30, color: 'white' }} />
            </Box>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                Dienst
              </Typography>
              <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                Firma de Documentos
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Contenido principal */}
      <Container maxWidth="md" sx={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center',
        py: 4
      }}>
        <Paper sx={{ 
          p: 4, 
          textAlign: 'center',
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)'
        }}>
        {/* Header con ícono de éxito */}
        <Box sx={{ mb: 4 }}>
          <CheckCircle 
            sx={{ 
              fontSize: 80, 
              color: estado === 'VERIFICADO' ? 'success.main' : 
                     estado === 'RECHAZADO' ? 'error.main' : 'warning.main',
              mb: 2
            }} 
          />
          <Typography variant="h4" gutterBottom color="primary">
            {estado === 'VERIFICADO' 
              ? '¡Verificación Completada!' 
              : estado === 'RECHAZADO'
              ? 'Verificación Rechazada'
              : 'Verificación Enviada Exitosamente'
            }
          </Typography>
          <Typography variant="h6" color="textSecondary">
            ID de Verificación: {verificacionId}
          </Typography>
        </Box>

        {/* Stepper del proceso */}
        <Box sx={{ mb: 4 }}>
          <Stepper activeStep={getCurrentStep()} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* Información del estado */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Schedule color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Tiempo Estimado</Typography>
                </Box>
                <Typography variant="h4" color="primary" gutterBottom>
                  {getTiempoEstimado()}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {automatica 
                    ? 'Tu verificación fue procesada automáticamente'
                    : 'Nuestro equipo revisará tu documentación'
                  }
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Security color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Estado Actual</Typography>
                </Box>
                <Typography variant="h4" color="primary" gutterBottom>
                  {estado === 'VERIFICADO' ? 'Aprobado' : 
                   estado === 'RECHAZADO' ? 'Rechazado' : 'En Revisión'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {estado === 'VERIFICADO' 
                    ? 'Tu identidad ha sido verificada correctamente'
                    : estado === 'RECHAZADO'
                    ? 'La verificación fue rechazada. Revisa tus documentos.'
                    : 'Estamos validando tu documentación'
                  }
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Alertas informativas */}
        {estado === 'PENDIENTE_APROBACION' && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body1" gutterBottom>
              <strong>¿Qué pasa ahora?</strong>
            </Typography>
            <Typography variant="body2">
              Nuestro equipo de verificación revisará tu documentación y selfie. 
              Te notificaremos por email y SMS cuando el proceso esté completo.
            </Typography>
          </Alert>
        )}

        {estado === 'VERIFICADO' && (
          <Alert severity="success" sx={{ mb: 3 }}>
            <Typography variant="body1" gutterBottom>
              <strong>¡Verificación Exitosa!</strong>
            </Typography>
            <Typography variant="body2">
              Tu identidad ha sido verificada automáticamente. 
              Ya puedes proceder con la firma del documento.
            </Typography>
          </Alert>
        )}

        {estado === 'RECHAZADO' && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="body1" gutterBottom>
              <strong>Verificación Rechazada</strong>
            </Typography>
            <Typography variant="body2">
              {onfidoResult?.message || 'La verificación fue rechazada. Por favor, verifica que tus documentos sean válidos y legibles.'}
            </Typography>
          </Alert>
        )}

        {/* Información adicional */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              📋 Documentos Revisados
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Description color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body2">Documento de Identidad</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Description color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body2">Selfie con Prueba de Vida</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Description color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body2">Verificación SMS</Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Información de contacto */}
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              📞 ¿Necesitas ayuda?
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Email color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    soporte@firmaelectronica.com
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Phone color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    +54 11 1234-5678
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Botones de acción */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={onVolver}
          >
            {estado === 'RECHAZADO' ? 'Intentar Nuevamente' : 'Volver'}
          </Button>
          
          {estado === 'VERIFICADO' && (
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={onContinuar}
            >
              Continuar con la Firma
            </Button>
          )}
        </Box>

        {/* Footer informativo */}
        <Divider sx={{ my: 3 }} />
        <Typography variant="body2" color="textSecondary">
          Este proceso cumple con los estándares de seguridad y verificación de identidad.
          Tus datos están protegidos y encriptados.
        </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default VerificacionEnviada; 